/**
 * Money status — three buckets on Billing. Owner, Nov 1 2026: "can you find a feature that shows all the paid-for
 * billings and add that as 'in transit' — because outstanding and collected; what about the people that have paid
 * but I don't have a payout for yet?"
 *
 *   Outstanding   every sent bill with a balance, the bill behind each
 *   In transit    paid by card, not yet landed in the bank — the charge is in no payout Stripe has marked paid
 *                 (server/services/moneyStatus reads the payouts of the last 90 days through the Stripe payouts card's
 *                 service). Cash / check / Zelle / Venmo are never here: they are collected the day they were written down.
 *   Collected     paid money that has landed, for this month / last month / 90 days
 *
 * Tapping a tile opens its list. Nothing here changes anything — read only, like the Stripe payouts card below it.
 */
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, RefreshCw, Truck } from 'lucide-react';
import { api } from '../services/api';
import { fmtTs } from '../utils/time';

type Range = 'this_month' | 'last_month' | '90d';
interface OpenRow { invoice_id: number; invoice_number: string; customer_id: number; customer_name: string; status: string; total: number; paid: number; balance: number; due_date: string | null; created_at: string | null; }
interface PayRow {
  payment_id: number; invoice_id: number; invoice_number: string; customer_id: number; customer_name: string;
  amount: number; tip: number; gross: number; method: string; card: boolean; paid_day: string | null; landed: string | null;
  arrival: string | null; payout_id: string | null; why: string; test: boolean;
}
interface Status {
  range: { range: Range; from: string; to: string; label: string }; today: string; days: number; since: string; fetched_at: string;
  stripe: { ok: boolean; words: string | null; on_stripe_side: number | null; balance: { available: number; pending: number } | null;
            next: { amount: number; arrival: string | null } | null; payouts_read: number; errors: string[] };
  outstanding: { total: number; count: number; rows: OpenRow[] };
  in_transit: { total: number; gross: number; tips: number; count: number; rows: PayRow[] };
  collected: { total: number; tips: number; count: number; rows: PayRow[]; card: number; other: number };
  excluded: { count: number; rows: PayRow[] };
  note: string | null;
}

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** "Oct 29" from a YYYY-MM-DD (a day, not an instant). */
const day = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const methodWords = (m: string) => ({ stripe: 'card', manual: 'by hand', cash: 'cash', check: 'check', zelle: 'Zelle', venmo: 'Venmo', paid_outside: 'paid outside' } as Record<string, string>)[String(m || '').toLowerCase()] || m;

type Bucket = 'outstanding' | 'in_transit' | 'collected';
const RANGES: { key: Range; label: string }[] = [{ key: 'this_month', label: 'This month' }, { key: 'last_month', label: 'Last month' }, { key: '90d', label: '90 days' }];

function Bill({ r }: { r: { customer_id: number; customer_name: string; invoice_id: number; invoice_number: string } }) {
  return (
    <span className="flex-1 min-w-0 truncate">
      <Link to={`/customers/${r.customer_id}`} className="text-brand-700 hover:underline">{r.customer_name}</Link>
      {' · '}<Link to={`/billing?invoice=${r.invoice_id}`} className="text-gray-600 hover:underline">{r.invoice_number}</Link>
    </span>
  );
}

export default function MoneyStatusCard() {
  const [range, setRange] = useState<Range>('this_month');
  const [open, setOpen] = useState<Bucket | null>(null);
  const { data, isFetching, refetch, error } = useQuery(['billing-money-status', range],
    () => api.get(`/billing/money-status?range=${range}`).then(r => r.data as Status),
    { retry: false, staleTime: 60 * 1000, refetchOnWindowFocus: false, keepPreviousData: true });
  if (error) return <div className="card p-4 text-sm text-red-700">{(error as any)?.response?.data?.message || 'Could not work out the money status'}</div>;
  if (!data) return <div className="card p-4 text-sm text-gray-500">Working out the money…</div>;

  const toggle = (b: Bucket) => setOpen(o => (o === b ? null : b));
  const tiles: { key: Bucket; label: string; total: number; sub: string; icon: any; color: string }[] = [
    { key: 'outstanding', label: 'Outstanding', total: data.outstanding.total, sub: `${plural(data.outstanding.count, 'bill')} still owed`, icon: AlertCircle, color: 'bg-blue-500' },
    { key: 'in_transit', label: 'In transit', total: data.in_transit.total, icon: Truck, color: 'bg-violet-500',
      sub: data.in_transit.count === 0 ? 'every card payment has landed'
        : `${plural(data.in_transit.count, 'card payment')} paid, not in the bank yet${data.stripe.next?.arrival ? ` · next payout lands ${day(data.stripe.next.arrival)}` : ''}` },
    { key: 'collected', label: 'Collected', total: data.collected.total, sub: `${plural(data.collected.count, 'payment')} ${data.range.label} · ${money(data.collected.card)} by card, ${money(data.collected.other)} other`, icon: CheckCircle, color: 'bg-green-500' },
  ];

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-900">Money status</h3>
          <p className="text-xs text-gray-500">Owed · paid but not in the bank yet · landed. Tap a tile for the list.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            {RANGES.map(r => (
              <button key={r.key} type="button" onClick={() => setRange(r.key)}
                className={`px-2.5 py-1 ${range === r.key ? 'bg-brand-600 text-black font-medium' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{r.label}</button>
            ))}
          </div>
          <button type="button" onClick={() => refetch()} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1" disabled={isFetching} title="Ask again">
            <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} /> {data.fetched_at ? `as of ${fmtTs(data.fetched_at, { hour: 'numeric', minute: '2-digit' })}` : 'refresh'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiles.map(t => (
          <button key={t.key} type="button" onClick={() => toggle(t.key)} aria-expanded={open === t.key}
            className={`text-left rounded-lg p-3 border transition-colors ${open === t.key ? 'border-brand-400 bg-brand-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'}`}>
            <div className="flex items-start gap-3">
              <div className={`rounded-lg p-2 ${t.color}`}><t.icon className="h-4 w-4 text-white" /></div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{t.label}</p>
                <p className="text-xl font-bold text-gray-900 tabular-nums">{money(t.total)}</p>
                <p className="text-[11px] text-gray-500 leading-tight mt-0.5">{t.sub}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {open === 'outstanding' && (
        <div className="space-y-1">
          {data.outstanding.rows.length === 0 && <div className="text-sm text-gray-500">Nothing owed.</div>}
          {data.outstanding.rows.map(r => (
            <div key={r.invoice_id} className="flex items-baseline gap-2 text-sm">
              <Bill r={r} />
              <span className="text-xs text-gray-500">{r.status === 'overdue' ? 'overdue' : r.due_date ? `due ${day(r.due_date)}` : 'sent'}{r.paid > 0 ? ` · ${money(r.paid)} paid of ${money(r.total)}` : ''}</span>
              <span className="tabular-nums font-medium">{money(r.balance)}</span>
            </div>
          ))}
        </div>
      )}

      {open === 'in_transit' && (
        <div className="space-y-1">
          {!data.stripe.ok && <div className="text-xs text-amber-700">{data.stripe.words || 'Stripe not read'} — card payments can't be told landed or not, so they wait here.</div>}
          {data.in_transit.rows.length === 0 && <div className="text-sm text-gray-500">Every card payment of the last {data.days} days is in a payout that landed.</div>}
          {data.in_transit.rows.map(r => (
            <div key={r.payment_id} className="flex items-baseline gap-2 text-sm">
              <span className="text-xs text-gray-400 w-12 shrink-0">{day(r.paid_day)}</span>
              <Bill r={r} />
              <span className="text-xs text-gray-500">{r.arrival ? `lands ${day(r.arrival)}` : data.stripe.ok ? 'next payout' : 'not read'}{r.tip > 0 ? ` · tip ${money(r.tip)}` : ''}{r.test ? ' · TEST' : ''}</span>
              <span className="tabular-nums font-medium">{money(r.amount)}</span>
            </div>
          ))}
          {data.in_transit.rows.length > 0 && (
            <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">Paid on the day shown; in transit until Stripe's payout with that charge is marked paid. Stripe's payouts card below shows the payouts themselves.</div>
          )}
        </div>
      )}

      {open === 'collected' && (
        <div className="space-y-1">
          {data.collected.rows.length === 0 && <div className="text-sm text-gray-500">Nothing collected {data.range.label}.</div>}
          {data.collected.rows.map(r => (
            <div key={r.payment_id} className="flex items-baseline gap-2 text-sm">
              <span className="text-xs text-gray-400 w-12 shrink-0">{day(r.paid_day)}</span>
              <Bill r={r} />
              <span className="text-xs text-gray-500">{r.card ? `card · landed ${day(r.landed)}` : methodWords(r.method)}{r.tip > 0 ? ` · tip ${money(r.tip)}` : ''}{r.test ? ' · TEST' : ''}</span>
              <span className="tabular-nums font-medium">{money(r.amount)}</span>
            </div>
          ))}
          {data.collected.tips > 0 && <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">Tips ({money(data.collected.tips)}) ride on the payment, not the bill — they are not in the total.</div>}
        </div>
      )}

      {data.note && <div className="text-xs text-gray-500">{data.note}</div>}
      {!!data.stripe.errors?.length && data.stripe.ok && <div className="text-xs text-amber-700">Stripe did not answer everything: {data.stripe.errors.join(' · ')}</div>}
    </div>
  );
}
